import React from 'react';

import { FormControl, InputLabel, MenuItem, Select, TextField, makeStyles } from '@material-ui/core';
import * as Elevated from '@pitaman71/omniglot-introspect';
import { Clients, Streams, Values } from '@pitaman71/omniglot-live-data';
import { Domains } from '@pitaman71/omniglot-live-domains';

import { Propose } from '..';

type BasisType = Elevated.getValueType<typeof Domains.Financial.BasisDomain>;
type AmountType = Elevated.getValueType<typeof Domains.Financial.AmountDomain>;
type CurrencyCodesType = Elevated.getValueType<typeof Domains.Currency.CodesDomain>;
type PayRangeType = Elevated.getValueType<typeof Domains.Financial.PayRangeDomain>;

const useStyles = makeStyles((theme) => ({
    formControl: {
      margin: theme.spacing(1),
      flex: "0 0 auto"
    },
    textField: {
        margin: theme.spacing(1),
        flex: "1"
      },
    selectEmpty: {
      marginTop: theme.spacing(2),
    }
}));

export function AsPayRange(props: {
    active?: boolean,
    domain?: Elevated.Domain<Partial<PayRangeType>>,
    value?: Partial<PayRangeType>,
    client?: Clients.ScalarClient<Partial<PayRangeType>>,
    autoFocus?: boolean,
    style?: React.CSSProperties
}) {    
    const classes = useStyles();
    const currencyClient = {
        assign: (currency_:CurrencyCodesType) => {
            const value_: Partial<PayRangeType> = { 
                basis: {},
                ...props.value,
                currency: currency_
             };
            props.client?.assign(value_)
            return currencyClient;
        }, clear: () => {
            const value_: Partial<PayRangeType> = { 
                ...props.value
             };
            delete value_.currency;
            props.client?.assign(value_);
            return currencyClient;
        }
    };
    const basisClient = {
        assign: (basis_:Values.Parseable<void> & { standard?: BasisType }) => {
            const value_: Partial<PayRangeType> = { 
                ...props.value,
                basis: basis_
            };
            props.client?.assign(value_);
            return basisClient;
        }, clear: () => {
            const value_: Partial<PayRangeType> = { ...props.value };
            delete value_.basis;
            props.client?.assign(value_)
            return basisClient;
        }
    };
    return (
        <div id="financial-payrange" style={{ display: 'inline-block', width: '100%', ...props.style }}>
            <AsCurrencySelector
                active={props.active}
                value={props.value?.currency}
                client={!props.client ? undefined : currencyClient}
                style={{ display: 'inline-flex', width: '8rem' }}
            />
            <AsBasisSelector
                active={props.active}
                value={props.value?.basis}
                client={!props.client ? undefined : basisClient}
                style={{ display: 'inline-flex', width: '8rem' }}
            />
            <TextField
                id="financial-payrange-amount" 
                variant="outlined"
                label="Amount"
                value={props.value?.minimum || ""}
                className={classes.textField}
                style={{ display: 'inline-flex', width: '12rem' }}
                onChange={props.active === false || props.client === undefined ? undefined : (event) => {
                    if(event.target.value === null || event.target.value === undefined) {
                        const value_:Partial<PayRangeType> = { 
                            ...props.value
                        };
                        if(value_) {
                            delete value_.minimum;
                            delete value_.maximum;
                        }
                        props.client?.assign(value_)
                    } else {
                        const value_: Partial<PayRangeType> = {
                            basis: {},
                            ...props.value,
                            minimum: Number.parseInt(event.target.value),
                            maximum: Number.parseInt(event.target.value)
                        };
                        props.client?.assign(value_);
                    }
                }}
            />
        </div>
    );
}
  
export function AsCurrencySelector(props: {
    active?: boolean,
    style?: React.CSSProperties
    value?: CurrencyCodesType,
    client?: Clients.ScalarClient<CurrencyCodesType>
}) {
    const client = props.client;
    const classes = useStyles();
    return (
        <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="currency-label">Currency</InputLabel>
            <Select
                labelId="currency-label"
                value={props.value === undefined ? "" : props.value}
                style={props.style}
                onChange={ props.active === false || !client ? undefined : (event) => {
                    if(event.target.value === undefined) {
                        client.clear();
                    } else {
                        const value_ = Domains.Currency.CodesDomain.asJSON()?.from(event.target.value as string);
                        if(value_) client.assign(value_);
                        client.clear();
                    }
                }}
            >
                {
                    Domains.Currency.CodesDomain.values.map(code => {
                        return (
                            <MenuItem id={`financial-currency-${code}`} value={code}>
                                {`${code}`}
                            </MenuItem>            
                        )
                    })
                }
            </Select>
        </FormControl>
    )
}

export function AsBasisSelector(props: {
    active?: boolean,
    style?: React.CSSProperties
    value?: { standard?: BasisType },
    client?: Clients.ScalarClient<{ standard?: BasisType }>
}) {
    const client = props.client;
    const classes = useStyles();
    return (
        <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="basis-label">Per</InputLabel>
            <Select
                labelId="basis-label"
                value={props.value === undefined ? "" : props.value.standard}
                style={props.style}
                onChange={ props.active === false || !client ? undefined : (event) => {
                    if(event.target.value === undefined) {
                        client.clear();
                    } else {
                        const value_ = Domains.Financial.BasisDomain.asJSON()?.from(event.target.value as string);
                        if(value_ === null) client.clear();
                        else client.assign({ standard: value_ })
                    }
                }}
            >
                {
                    Domains.Financial.BasisDomain.values.map(value_ => {
                        return (
                            <MenuItem id={`financial-basis-${value_}`} value={value_}>
                                {`${value_}`}
                            </MenuItem>            
                        )
                    })
                }
            </Select>
        </FormControl>
    )
}